import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  DollarSign, TrendingUp, AlertCircle, Clock, CheckCircle,
  ChevronRight, X, Loader2, Building2, Calendar, FileText,
  ArrowDownToLine, Landmark, ShieldCheck, Archive, Plus,
  CreditCard, ChevronDown, ChevronUp, Hammer
} from "lucide-react";
import { DealServicingPanel } from "../shared/ServicingPanel";
import RecordPaymentModal from "../shared/RecordPaymentModal";
import ServicingRevenueDashboard from "./ServicingRevenueDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt$ = (v) => {
  if (v == null || v === 0) return "$0";
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "--";

export default function AdminServicingTab() {
  const [portfolio, setPortfolio] = useState(null);
  const [closedDeals, setClosedDeals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [dealServicing, setDealServicing] = useState(null);
  const [dealLoading, setDealLoading] = useState(false);
  const [payoffQuote, setPayoffQuote] = useState(null);
  const [payoffLoading, setPayoffLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [subTab, setSubTab] = useState("active");

  const fetchPortfolio = useCallback(async () => {
    try {
      const [portfolioRes, closedRes] = await Promise.all([
        axios.get(`${API}/servicing/admin/portfolio`),
        axios.get(`${API}/servicing/admin/closed-deals`).catch(() => ({ data: { deals: [] } })),
      ]);
      setPortfolio(portfolioRes.data);
      setClosedDeals(closedRes.data);
    } catch (err) {
      console.error("Portfolio fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPortfolio(); }, [fetchPortfolio]);

  const openDealPanel = async (deal) => {
    setSelectedDeal(deal);
    setDealLoading(true);
    setPayoffQuote(null);
    try {
      const res = await axios.get(`${API}/servicing/deal/${deal.lead_id}`);
      setDealServicing(res.data);
    } catch (err) {
      console.error("Deal servicing fetch error:", err);
      setDealServicing(null);
    } finally {
      setDealLoading(false);
    }
  };

  const generatePayoff = async (leadId) => {
    setPayoffLoading(true);
    try {
      const res = await axios.get(`${API}/servicing/deal/${leadId}/payoff-quote`);
      setPayoffQuote(res.data);
    } catch (err) {
      console.error("Payoff quote error:", err);
    } finally {
      setPayoffLoading(false);
    }
  };

  const handlePaymentRecorded = () => {
    setShowPaymentModal(false);
    if (selectedDeal) openDealPanel(selectedDeal);
    fetchPortfolio();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="servicing-tab-loading">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-700" />
      </div>
    );
  }

  const summary = portfolio?.summary || {};
  const deals = portfolio?.deals || [];
  const closed = closedDeals?.deals || [];

  return (
    <div className="space-y-5" data-testid="admin-servicing-tab">
      {/* Sub-tabs */}
      <div className="flex items-center gap-1 p-1 rounded-xl bg-[#f5f0e8] border border-[#e8e0d0] w-fit" data-testid="servicing-sub-tabs">
        <button onClick={() => setSubTab("active")} data-testid="servicing-subtab-active"
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${subTab === "active" ? "bg-white text-emerald-700 shadow-sm border border-emerald-200" : "text-[#888] hover:text-[#555]"}`}>
          Active Loans ({deals.length})
        </button>
        <button onClick={() => setSubTab("revenue")} data-testid="servicing-subtab-revenue"
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${subTab === "revenue" ? "bg-white text-[#0A0A0A] shadow-sm border border-emerald-300" : "text-[#888] hover:text-[#555]"}`}>
          <span className="flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5" /> Revenue</span>
        </button>
        <button onClick={() => setSubTab("closed")} data-testid="servicing-subtab-closed"
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${subTab === "closed" ? "bg-white text-[#8B7340] shadow-sm border border-[#E5C98B]" : "text-[#888] hover:text-[#555]"}`}>
          <span className="flex items-center gap-1.5"><Archive className="h-3.5 w-3.5" /> Closed ({closed.length})</span>
        </button>
      </div>

      {/* Portfolio KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="servicing-kpis">
        {[
          { label: "Funded Deals", value: summary.total_funded_deals || 0, icon: ShieldCheck, color: "text-emerald-600" },
          { label: "Total Principal", value: fmt$(summary.total_principal), icon: DollarSign, color: "text-[#0A0A0A]" },
          { label: "Remaining Principal", value: fmt$(summary.remaining_principal), icon: Landmark, color: "text-blue-600" },
          { label: "Total Paid", value: fmt$(summary.total_paid), icon: CheckCircle, color: "text-emerald-600" },
          { label: "Interest Paid", value: fmt$(summary.total_interest_paid), icon: TrendingUp, color: "text-amber-600" },
          { label: "Principal Paid", value: fmt$(summary.total_principal_paid), icon: ArrowDownToLine, color: "text-indigo-600" },
          { label: "Overdue", value: summary.overdue_count || 0, icon: AlertCircle, color: summary.overdue_count > 0 ? "text-red-600" : "text-[#999]" },
        ].map((kpi, i) => (
          <motion.div key={kpi.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
            className="rounded-xl border border-[#e8e0d0] bg-white p-3" data-testid={`servicing-kpi-${kpi.label.toLowerCase().replace(/ /g, '-')}`}>
            <div className="flex items-center gap-2 mb-1">
              <kpi.icon className={`h-3.5 w-3.5 ${kpi.color}`} />
              <span className="text-[9px] uppercase tracking-wider text-[#999] font-semibold">{kpi.label}</span>
            </div>
            <div className={`text-lg font-bold ${kpi.color}`}>{kpi.value}</div>
          </motion.div>
        ))}
      </div>

      {/* Active Loans Table */}
      {subTab === "active" && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white overflow-hidden" data-testid="servicing-deals-table">
          {deals.length === 0 ? (
            <div className="p-12 text-center">
              <Landmark className="h-8 w-8 text-[#ccc] mx-auto mb-3" />
              <p className="text-sm text-[#888]">No funded deals yet. Deals appear here once they reach the Funded stage.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#faf7f2] border-b border-[#e8e0d0]">
                    <th className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-[#888] font-semibold">Deal</th>
                    <th className="px-3 py-3 text-left text-[10px] uppercase tracking-wider text-[#888] font-semibold">Borrower</th>
                    <th className="px-3 py-3 text-right text-[10px] uppercase tracking-wider text-[#888] font-semibold">Funded</th>
                    <th className="px-3 py-3 text-center text-[10px] uppercase tracking-wider text-[#888] font-semibold">Rate</th>
                    <th className="px-3 py-3 text-right text-[10px] uppercase tracking-wider text-[#888] font-semibold">Paid</th>
                    <th className="px-3 py-3 text-right text-[10px] uppercase tracking-wider text-[#888] font-semibold">Remaining</th>
                    <th className="px-3 py-3 text-center text-[10px] uppercase tracking-wider text-[#888] font-semibold">Maturity</th>
                    <th className="px-3 py-3 text-center text-[10px] uppercase tracking-wider text-[#888] font-semibold">Status</th>
                    <th className="px-3 py-3 text-center text-[10px] uppercase tracking-wider text-[#888] font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deals.map((deal, i) => (
                    <tr key={deal.lead_id} className="border-b border-[#f5f0e8] hover:bg-[#fdfcfa] cursor-pointer transition-colors"
                      onClick={() => openDealPanel(deal)} data-testid={`servicing-deal-row-${i}`}>
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="font-bold text-[#0A0A0A]">{deal.deal_number}</span>
                          <span className="text-[10px] text-[#888] truncate max-w-[200px]">{deal.property_address}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col">
                          <span className="text-[#333] font-medium">{deal.borrower_name || "—"}</span>
                          <span className="text-[10px] text-[#888]">{deal.lender_name || "No lender"}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right font-bold text-[#0A0A0A]">{fmt$(deal.funded_amount)}</td>
                      <td className="px-3 py-3 text-center">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                          {deal.rate}% / {deal.term_months}mo
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right font-semibold text-emerald-600">{fmt$(deal.total_paid)}</td>
                      <td className="px-3 py-3 text-right font-semibold text-[#333]">{fmt$(deal.remaining_principal)}</td>
                      <td className="px-3 py-3 text-center text-xs text-[#666]">{fmtDate(deal.maturity_date)}</td>
                      <td className="px-3 py-3 text-center">
                        {deal.is_overdue ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-red-50 text-red-600 border border-red-200">
                            <AlertCircle className="h-2.5 w-2.5" /> Overdue
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-200">
                            <CheckCircle className="h-2.5 w-2.5" /> Current
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center" onClick={e => e.stopPropagation()}>
                        <ChevronRight className="h-4 w-4 text-[#999] mx-auto" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Revenue Dashboard */}
      {subTab === "revenue" && <ServicingRevenueDashboard />}

      {/* Closed Deals Archive */}
      {subTab === "closed" && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white overflow-hidden" data-testid="closed-deals-table">
          {closed.length === 0 ? (
            <div className="p-12 text-center">
              <Archive className="h-8 w-8 text-[#ccc] mx-auto mb-3" />
              <p className="text-sm text-[#888]">No closed or matured deals yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#faf7f2] border-b border-[#e8e0d0]">
                    <th className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-[#888] font-semibold">Deal</th>
                    <th className="px-3 py-3 text-left text-[10px] uppercase tracking-wider text-[#888] font-semibold">Borrower</th>
                    <th className="px-3 py-3 text-right text-[10px] uppercase tracking-wider text-[#888] font-semibold">Original Amount</th>
                    <th className="px-3 py-3 text-right text-[10px] uppercase tracking-wider text-[#888] font-semibold">Total Paid</th>
                    <th className="px-3 py-3 text-center text-[10px] uppercase tracking-wider text-[#888] font-semibold">Payments</th>
                    <th className="px-3 py-3 text-center text-[10px] uppercase tracking-wider text-[#888] font-semibold">Closed Date</th>
                  </tr>
                </thead>
                <tbody>
                  {closed.map((deal, i) => (
                    <tr key={deal.lead_id} className="border-b border-[#f5f0e8]" data-testid={`closed-deal-row-${i}`}>
                      <td className="px-4 py-3">
                        <span className="font-bold text-[#333]">{deal.deal_number}</span>
                        <div className="text-[10px] text-[#888]">{deal.property_address}</div>
                      </td>
                      <td className="px-3 py-3 text-[#333]">{deal.borrower_name || "—"}</td>
                      <td className="px-3 py-3 text-right font-semibold text-[#333]">{fmt$(deal.funded_amount)}</td>
                      <td className="px-3 py-3 text-right font-semibold text-emerald-600">{fmt$(deal.total_paid)}</td>
                      <td className="px-3 py-3 text-center text-[#666]">{deal.payment_count}</td>
                      <td className="px-3 py-3 text-center text-xs text-[#666]">{fmtDate(deal.closed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Slide-in Detail Panel */}
      <AnimatePresence>
        {selectedDeal && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
              onClick={() => setSelectedDeal(null)}
              data-testid="servicing-panel-overlay"
            />
            <motion.div
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl z-50 overflow-y-auto"
              data-testid="servicing-detail-panel"
            >
              {/* Panel Header */}
              <div className="sticky top-0 z-10 bg-white border-b border-[#e8e0d0] px-5 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-bold text-[#0A0A0A]" data-testid="panel-deal-number">{selectedDeal.deal_number}</h2>
                    <p className="text-xs text-[#888]">{selectedDeal.property_address} — {selectedDeal.borrower_name}</p>
                  </div>
                  <button onClick={() => setSelectedDeal(null)} className="p-2 rounded-lg hover:bg-[#f5f0e8]" data-testid="close-servicing-panel">
                    <X className="h-5 w-5 text-[#666]" />
                  </button>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => setShowPaymentModal(true)}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition-colors"
                    data-testid="panel-record-payment-btn"
                  >
                    <Plus className="h-3.5 w-3.5" /> Record Payment
                  </button>
                  <button
                    onClick={() => generatePayoff(selectedDeal.lead_id)}
                    disabled={payoffLoading}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#0A0A0A] text-white text-xs font-bold hover:bg-[#222] transition-colors disabled:opacity-50"
                    data-testid="panel-payoff-quote-btn"
                  >
                    {payoffLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                    Payoff Quote
                  </button>
                </div>
              </div>

              {/* Panel Content */}
              <div className="p-5 space-y-4">
                {dealLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                  </div>
                ) : dealServicing ? (
                  <DealServicingPanel
                    servicing={dealServicing}
                    isAdmin={true}
                    leadId={selectedDeal.lead_id}
                    dealNumber={selectedDeal.deal_number}
                    onPaymentRecorded={handlePaymentRecorded}
                  />
                ) : (
                  <div className="text-center py-8 text-sm text-[#888]">Unable to load servicing data.</div>
                )}

                {/* Draw Schedule Placeholder (for construction deals) */}
                {selectedDeal.loan_type === "ground_up_construction" && (
                  <div className="rounded-xl border-2 border-dashed border-[#e8e0d0] bg-[#faf7f2] p-6 text-center" data-testid="draw-schedule-placeholder">
                    <Hammer className="h-8 w-8 text-[#ccc] mx-auto mb-2" />
                    <h3 className="text-sm font-bold text-[#666] mb-1">Draw Schedule</h3>
                    <p className="text-xs text-[#999]">Construction draw schedule management coming soon.</p>
                    <p className="text-[10px] text-[#bbb] mt-1">Track inspection milestones, draw requests, and disbursements.</p>
                  </div>
                )}

                {/* Payoff Quote Display */}
                {payoffQuote && <PayoffQuoteCard quote={payoffQuote} onClose={() => setPayoffQuote(null)} />}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Record Payment Modal */}
      {showPaymentModal && selectedDeal && (
        <RecordPaymentModal
          leadId={selectedDeal.lead_id}
          dealNumber={selectedDeal.deal_number}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={handlePaymentRecorded}
        />
      )}
    </div>
  );
}


function PayoffQuoteCard({ quote, onClose }) {
  const fmt$ = (v) => {
    if (v == null) return "$0.00";
    return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border-2 border-[#0A0A0A] bg-white p-5 relative" data-testid="payoff-quote-card">
      <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded hover:bg-[#f5f0e8]" data-testid="close-payoff-quote">
        <X className="h-4 w-4 text-[#999]" />
      </button>

      <div className="flex items-center gap-2 mb-4">
        <FileText className="h-5 w-5 text-[#0A0A0A]" />
        <h3 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider">Payoff Quote</h3>
      </div>

      <div className="space-y-3">
        <div className="flex justify-between text-xs">
          <span className="text-[#666]">Deal</span>
          <span className="font-bold text-[#0A0A0A]">{quote.deal_number}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-[#666]">Borrower</span>
          <span className="font-medium text-[#333]">{quote.borrower_name}</span>
        </div>

        <div className="h-px bg-[#e8e0d0]" />

        <div className="flex justify-between text-xs">
          <span className="text-[#666]">Outstanding Principal</span>
          <span className="font-bold text-[#0A0A0A]">{fmt$(quote.outstanding_principal)}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-[#666]">Accrued Interest ({quote.days_accrued} days)</span>
          <span className="font-semibold text-amber-700">{fmt$(quote.accrued_interest)}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-[#666]">Per Diem</span>
          <span className="text-[#888]">{fmt$(quote.per_diem)}/day</span>
        </div>

        <div className="h-px bg-[#0A0A0A]" />

        <div className="flex justify-between text-sm">
          <span className="font-bold text-[#0A0A0A]">Total Payoff Amount</span>
          <span className="font-black text-lg text-emerald-700">{fmt$(quote.total_payoff)}</span>
        </div>

        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mt-2">
          <div className="flex justify-between text-xs">
            <span className="text-amber-700 font-semibold">Good Through</span>
            <span className="font-bold text-amber-800">{quote.good_through_date}</span>
          </div>
          <p className="text-[10px] text-amber-600 mt-1">
            After this date, add {fmt$(quote.per_diem)} per diem for each additional day.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-2 text-[10px] text-[#888]">
          <div>Rate: {quote.annual_rate}% | Term: {quote.term_months}mo</div>
          <div className="text-right">Maturity: {quote.maturity_date}</div>
          <div>Payments Made: {quote.payments_made}</div>
          <div className="text-right">Generated: {new Date(quote.generated_at).toLocaleDateString()}</div>
        </div>
      </div>
    </motion.div>
  );
}
